import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ForaeldreloestAfregningsgrundlagService, ServiceAftaleService, ServiceordreService } from 'src/app/api/services';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { finalize, takeUntil, flatMap } from 'rxjs/operators';
import { LoaderService } from 'src/app/services/loader.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, defer, BehaviorSubject, forkJoin, EMPTY, of } from 'rxjs';
import { GetServiceaftalerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-serviceaftaler-request';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ForaeldreloestAfregningsgrundlagDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/foraeldreloest-afregningsgrundlag-dto';
import { OpgaveTypeDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opgave-type-dto';
import { UntypedFormGroup, UntypedFormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { ForaeldreloestAfregningsgrundlagUpdateDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/foraeldreloest-afregningsgrundlag-update-dto';
import { GetServiceaftaleResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceaftale-response';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { MatDialog } from '@angular/material/dialog';
import { RejectPosteringerDialogComponent } from '../../../reject-posteringer-dialog/reject-posteringer-dialog.component';
import { RejectionData } from 'src/app/components/aftale/models';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { inOutAnimation } from 'src/app/common/animations';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-unparented-order-details',
  templateUrl: './unparented-order-details.component.html',
  styleUrls: ['./unparented-order-details.component.scss'],
  animations: [inOutAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UnparentedOrderDetailsComponent implements OnInit {
  constructor(
    private unparentedOrdreService: ForaeldreloestAfregningsgrundlagService,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private serviceAftaleService: ServiceAftaleService,
    private serviceOrdreService: ServiceordreService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private notifications: NotificationService,
    private dialog: MatDialog,
    private notification: NotificationService,
    private auth: AuthService,
  ) {
    this.canMatch = this.auth.isAdministrator || this.auth.isSuperuser;
  }

  @Input() unparentedAccounting: ForaeldreloestAfregningsgrundlagDto;
  searchQuery: string;
  serviceaftaler: ServiceaftaleDto[];
  dataSource = new BehaviorSubject<ServiceaftaleDto[]>([]);
  quickViewOpen: boolean = false;
  selectedaftale: ServiceaftaleDto;
  globalActiveRowID: number;
  opgaveTyper: OpgaveTypeDto[];
  hasBeenMatched = false;
  showUnknownKundeNrError = false;
  canMatch: boolean = false;

  displayedColumns: string[] = [
    'Serviceabonnement.Produkt',
    'ServiceaftaleState',
    'kundenr',
    'OkKundeAftaleNr',
    'kundeNavn',
    'adresse',
    'telefon',
    'Ajourtid',
    'Oprettid',
    'rowActions',
  ];

  unparentedOrdreForm: UntypedFormGroup = this.formBuilder.group({
    opgaveTypeId: new UntypedFormControl(null, [Validators.required]),
    deadline: new UntypedFormControl(new Date(), [Validators.required]),
    okKundeAftalenr: new UntypedFormControl(null, [Validators.required]),
  });

  get okKundeAftalenr() {
    return this.unparentedOrdreForm.get('okKundeAftalenr');
  }

  get deadline() {
    return this.unparentedOrdreForm.get('deadline');
  }

  get opgaveTypeId() {
    return this.unparentedOrdreForm.get('opgaveTypeId');
  }

  matchedServiceAftale: ServiceaftaleDto;

  unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    const unParentedId = Number(this.route.snapshot.paramMap.get('unParentedId'));

    this.loader.show();
    forkJoin([
      this.unparentedOrdreService
        .getById({
          id: unParentedId,
          caller: EGIS_SERVICE_CALLER,
        })
        .pipe(
          flatMap((unparented) => {
            this.unparentedAccounting = unparented;
            this.opgaveTypeId.setValue(unparented.opgaveTypeId);
            this.deadline.setValue(new Date(unparented.deliveryDate));
            if (unparented.okKundeAftalenr) {
              return this.serviceAftaleService.getServiceAftaleForKunde({
                caller: EGIS_SERVICE_CALLER,
                okKundeAftaleNr: unparented.okKundeAftalenr,
              });
            }
            return of<GetServiceaftaleResponse>(null);
          }),
        ),
      this.serviceOrdreService.getServiceordreOpgaveTyper({ caller: EGIS_SERVICE_CALLER }),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(
        (response) => {
          if (response[0]?.serviceaftale) {
            this.matchToServiceAftale(response[0].serviceaftale);
          } else {
            this.showUnknownKundeNrError = true;
          }
          this.opgaveTyper = response[1];
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.route.queryParams
      .pipe(
        flatMap((queryParams: Params) => {
          this.searchQuery = '';

          const q = queryParams['q'];
          if (q && !this.hasBeenMatched) {
            this.searchQuery = q;
            return this.getServiceaftaler$;
          }
          return EMPTY;
        }),
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(
        (response) => {
          this.serviceaftaler = response.serviceaftaler;
          this.dataSource.next(this.serviceaftaler);
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  getServiceaftaler$ = defer(() => {
    this.loader.show();
    const request: GetServiceaftalerRequest = {
      searchQuery: this.searchQuery,
    };

    return this.serviceAftaleService.getServiceaftaler({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      finalize(() => {
        this.loader.hide();
      }),
      takeUntil(this.unsubscribe$),
    );
  });

  saveUnparentedOrdre(approve: boolean = false) {
    if (new Date(this.deadline.value) < new Date(this.matchedServiceAftale.startDate)) {
      this.notifications.warning('Den valgte deadline ligger før serviceaftalens start, og de kan derfor ikke matches');
      return;
    }

    if (this.matchedServiceAftale.endDate != null && new Date(this.deadline.value) > new Date(this.matchedServiceAftale.endDate)) {
      this.notifications.warning('Den valgte deadline ligger efter serviceaftalens udløb, og de kan derfor ikke matches');
      return;
    }

    let update: ForaeldreloestAfregningsgrundlagUpdateDto = {
      deliveryDate: this.deadline.value.toOkDateString(),
      id: this.unparentedAccounting.id,
      opgaveTypeId: this.opgaveTypeId.value,
      okKundeAftalenr: this.okKundeAftalenr.value,
      approve: approve,
    };
    this.loader.show();
    this.unparentedOrdreService
      .updateForaeldreloestAfregningsgrundlag({
        caller: EGIS_SERVICE_CALLER,
        body: update,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(
        (response: ForaeldreloestAfregningsgrundlagDto) => {
          this.unparentedAccounting = response;
          this.router.navigate(['/ordre'], { fragment: '1' });
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  onRowClick(row: ServiceaftaleDto) {
    this.selectedaftale = row;
    this.globalActiveRowID = row.id;
    this.quickViewOpen = true;
  }

  getProductIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  getStatusIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale.isActive ? 'checkmark' : 'ban';
  }

  closeQuickView() {
    this.quickViewOpen = false;
    this.globalActiveRowID = null;
  }

  matchToServiceAftale(serviceAftale: ServiceaftaleDto) {
    this.okKundeAftalenr.setValue(serviceAftale.okKundeAftaleNr);
    this.matchedServiceAftale = serviceAftale;
    this.dataSource.next([serviceAftale]);
    this.hasBeenMatched = true;
  }

  unmatchServiceAftale() {
    this.okKundeAftalenr.reset();
    this.matchedServiceAftale = null;
    this.hasBeenMatched = false;
    this.dataSource.next(this.serviceaftaler);
  }

  rejectOrdre() {
    const dialogRef = this.dialog.open(RejectPosteringerDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
    });
    dialogRef.afterClosed().subscribe((rejection: RejectionData) => {
      if (rejection.confirm) {
        this.loader.show();
        this.unparentedOrdreService
          .rejectPartnerCreatedOrder({
            caller: EGIS_SERVICE_CALLER,
            body: {
              id: this.unparentedAccounting.id,
              rejectionRemark: rejection.rejectionRemark,
            },
          })
          .pipe(
            finalize(() => {
              this.loader.hide();
            }),
            takeUntil(this.unsubscribe$),
          )
          .subscribe(
            () => {
              this.router.navigate(['/foraeldreloese']);
            },
            (error) => {
              this.errorHandler.HandleError(error);
            },
          );
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
